import { macroRegionOf } from './format';
import {
  CAPACITY_BANDS,
  MACRO_REGIONS,
  POWER_CATEGORIES,
  SERVICE_CATEGORIES,
  type CapacityBand,
  type MacroRegion,
  type PowerCategory,
  type PublicListing,
  type ServiceCategory,
} from './types';

export const MAX_COMPARE = 3;

export interface ExploreSearch {
  region?: MacroRegion;
  /** Minimum capacity band the seeker needs. */
  capacity?: CapacityBand;
  /** Also match on expansion potential when filtering by capacity. */
  growth?: boolean;
  service?: ServiceCategory;
  power?: PowerCategory;
  /** Public slugs picked for comparison, at most MAX_COMPARE. */
  compare?: string[];
}

function pick<T extends string>(allowed: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

export function parseSlugList(value: unknown): string[] | undefined {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const slugs = raw
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter((v) => /^[a-z0-9-]{1,80}$/.test(v));
  const unique = [...new Set(slugs)].slice(0, MAX_COMPARE);
  return unique.length > 0 ? unique : undefined;
}

export function validateExploreSearch(search: Record<string, unknown>): ExploreSearch {
  const out: ExploreSearch = {};
  const region = pick(MACRO_REGIONS, search.region);
  const capacity = pick(CAPACITY_BANDS, search.capacity);
  const service = pick(SERVICE_CATEGORIES, search.service);
  const power = pick(POWER_CATEGORIES, search.power);
  const compare = parseSlugList(search.compare);
  if (region) out.region = region;
  if (capacity) out.capacity = capacity;
  if (capacity && (search.growth === true || search.growth === 'true')) out.growth = true;
  if (service) out.service = service;
  if (power) out.power = power;
  if (compare) out.compare = compare;
  return out;
}

const bandIndex = (band: CapacityBand) => CAPACITY_BANDS.indexOf(band);

export interface FilterResult {
  listings: PublicListing[];
  /** Listings left out only because a filtered fact is unknown or not disclosed. */
  hiddenByDisclosure: number;
}

export function filterListings(all: PublicListing[], search: ExploreSearch): FilterResult {
  let hiddenByDisclosure = 0;
  const listings = all.filter((l) => {
    if (search.region && macroRegionOf(l) !== search.region) return false;
    if (search.service && !l.service_categories.includes(search.service)) return false;

    let undisclosed = false;
    if (search.capacity) {
      const min = bandIndex(search.capacity);
      const now = l.capacity.disclosure === 'known' ? bandIndex(l.capacity.value.band) : -1;
      const later =
        search.growth && l.expansion.disclosure === 'known' ? bandIndex(l.expansion.value.band) : -1;
      if (now < min && later < min) {
        const couldMatch =
          l.capacity.disclosure !== 'known' || (search.growth && l.expansion.disclosure !== 'known');
        if (!couldMatch) return false;
        undisclosed = true;
      }
    }
    if (search.power) {
      if (l.power_categories.disclosure !== 'known') undisclosed = true;
      else if (!l.power_categories.value.includes(search.power)) return false;
    }
    if (undisclosed) {
      hiddenByDisclosure += 1;
      return false;
    }
    return true;
  });
  return { listings, hiddenByDisclosure };
}

export function countByRegion(listings: PublicListing[]): Record<MacroRegion, number> {
  const counts = Object.fromEntries(MACRO_REGIONS.map((r) => [r, 0])) as Record<MacroRegion, number>;
  for (const l of listings) {
    const region = macroRegionOf(l);
    if (region) counts[region] += 1;
  }
  return counts;
}

export function hasActiveFilters(search: ExploreSearch): boolean {
  return Boolean(search.region || search.capacity || search.service || search.power);
}
